import { useCallback, useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../services/AdminApiService';
import type { AdminDashboard, AdminUser } from '../types/admin';
import type { Board, Card } from '../types/board';
import type { Feedback, FeedbackReviewStatus } from '../types/feedback';
import { useAuth } from '../contexts/AuthContext';

export function AdminPage(): JSX.Element {
  const { user, sessionToken } = useAuth();
  const [dashboard, setDashboard] = useState<AdminDashboard | null>(null);
  const [feedback, setFeedback] = useState<Feedback[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingBoardId, setDeletingBoardId] = useState<string | null>(null);
  const [updatingFeedbackId, setUpdatingFeedbackId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.role !== 'admin' || !sessionToken) {
      setDashboard(null);
      setFeedback(null);
      return;
    }

    let isActive = true;
    setIsLoading(true);
    setError(null);

    Promise.all([
      adminApi.getDashboard(sessionToken),
      adminApi.getFeedback(sessionToken),
    ])
      .then(([dashboardResult, feedbackResult]) => {
        if (isActive) {
          setDashboard(dashboardResult);
          setFeedback(feedbackResult);
        }
      })
      .catch((loadError: unknown) => {
        if (isActive) {
          setError(loadError instanceof Error ? loadError.message : 'Failed to load admin dashboard');
        }
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [sessionToken, user?.role]);

  const totalCards = useMemo(() => (
    dashboard?.boards.reduce((count, board) => count + countBoardCards(board), 0) ?? 0
  ), [dashboard]);

  const totalFeedback = feedback?.length ?? 0;

  const handleDeleteBoard = useCallback((board: Board): void => {
    if (!sessionToken) return;
    const confirmed = window.confirm(`Permanently delete "${board.name}"? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingBoardId(board.id);
    setError(null);
    adminApi.deleteBoard(sessionToken, board.id)
      .then((deletedBoardId) => {
        setDashboard((current) => current
          ? {
            ...current,
            boards: current.boards.filter((currentBoard) => currentBoard.id !== deletedBoardId),
          }
          : current);
      })
      .catch((deleteError: unknown) => {
        setError(deleteError instanceof Error ? deleteError.message : 'Failed to delete board');
      })
      .finally(() => {
        setDeletingBoardId(null);
      });
  }, [sessionToken]);

  const handleReviewFeedback = useCallback((feedbackItem: Feedback, status: FeedbackReviewStatus): void => {
    if (!sessionToken) return;

    setUpdatingFeedbackId(feedbackItem.id);
    setError(null);
    adminApi.reviewFeedback(sessionToken, feedbackItem.id, status)
      .then((updatedFeedback) => {
        setFeedback((current) => current
          ? current.map((item) => item.id === updatedFeedback.id ? updatedFeedback : item)
          : current);
      })
      .catch((reviewError: unknown) => {
        setError(reviewError instanceof Error ? reviewError.message : 'Failed to update feedback');
      })
      .finally(() => {
        setUpdatingFeedbackId(null);
      });
  }, [sessionToken]);

  if (!user) {
    return (
      <AdminShell title="Admin Panel">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          Sign in with a Google administrator account to continue.
        </p>
      </AdminShell>
    );
  }

  if (user.role !== 'admin') {
    return (
      <AdminShell title="Admin Panel">
        <p className="text-sm font-medium text-red-700 dark:text-red-300">
          Your account does not have system administrator access.
        </p>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Admin Panel">
      {isLoading && (
        <p className="text-sm text-gray-600 dark:text-gray-300">Loading dashboard...</p>
      )}

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300">
          {error}
        </div>
      )}

      {dashboard && feedback && (
        <div className="space-y-8">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Metric label="Boards" value={dashboard.boards.length} />
            <Metric label="Cards" value={totalCards} />
            <Metric label="Google Users" value={dashboard.users.length} />
            <Metric label="Feedback" value={totalFeedback} />
          </div>

          <FeedbackSection
            feedback={feedback}
            updatingFeedbackId={updatingFeedbackId}
            onReview={handleReviewFeedback}
          />

          <section className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Boards</h2>
            </div>

            <div className="divide-y divide-gray-200 overflow-hidden rounded-lg border border-gray-200 bg-white dark:divide-gray-800 dark:border-gray-800 dark:bg-gray-900">
              {dashboard.boards.length === 0 ? (
                <p className="px-4 py-5 text-sm text-gray-600 dark:text-gray-300">No boards created yet.</p>
              ) : dashboard.boards.map((board) => (
                <BoardAdminRow
                  key={board.id}
                  board={board}
                  isDeleting={deletingBoardId === board.id}
                  onDelete={handleDeleteBoard}
                />
              ))}
            </div>
          </section>

          <section className="space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Google Users</h2>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
              <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-800">
                <thead className="bg-gray-50 dark:bg-gray-950">
                  <tr>
                    <HeaderCell>Name</HeaderCell>
                    <HeaderCell>Email</HeaderCell>
                    <HeaderCell>Role</HeaderCell>
                    <HeaderCell>Last Updated</HeaderCell>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
                  {dashboard.users.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-5 text-gray-600 dark:text-gray-300">
                        No Google users have signed in yet.
                      </td>
                    </tr>
                  ) : dashboard.users.map((adminUser) => (
                    <UserRow key={adminUser.id} user={adminUser} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      )}
    </AdminShell>
  );
}

interface AdminShellProps {
  children: ReactNode;
  title: string;
}

function AdminShell({ children, title }: AdminShellProps): JSX.Element {
  return (
    <div className="min-h-[calc(100vh-4.5rem)] bg-gray-50 px-4 py-8 dark:bg-gray-950 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h1>
        </div>
        {children}
      </div>
    </div>
  );
}

interface MetricProps {
  label: string;
  value: number;
}

function Metric({ label, value }: MetricProps): JSX.Element {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 dark:border-gray-800 dark:bg-gray-900">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

interface FeedbackSectionProps {
  feedback: Feedback[];
  updatingFeedbackId: string | null;
  onReview: (feedbackItem: Feedback, status: FeedbackReviewStatus) => void;
}

function FeedbackSection({ feedback, updatingFeedbackId, onReview }: FeedbackSectionProps): JSX.Element {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Feedback</h2>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-800">
          <thead className="bg-gray-50 dark:bg-gray-950">
            <tr>
              <HeaderCell>Message</HeaderCell>
              <HeaderCell>Email</HeaderCell>
              <HeaderCell>Status</HeaderCell>
              <HeaderCell>Submitted</HeaderCell>
              <HeaderCell>Actions</HeaderCell>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {feedback.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-5 text-gray-600 dark:text-gray-300">
                  No feedback submitted yet.
                </td>
              </tr>
            ) : feedback.map((feedbackItem) => (
              <FeedbackRow
                key={feedbackItem.id}
                feedback={feedbackItem}
                isUpdating={updatingFeedbackId === feedbackItem.id}
                onReview={onReview}
              />
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface FeedbackRowProps {
  feedback: Feedback;
  isUpdating: boolean;
  onReview: (feedbackItem: Feedback, status: FeedbackReviewStatus) => void;
}

function FeedbackRow({ feedback, isUpdating, onReview }: FeedbackRowProps): JSX.Element {
  const canReview = feedback.status === 'pending';

  return (
    <tr>
      <td className="px-4 py-3 align-top">
        <p className="max-w-xl whitespace-pre-wrap break-words text-gray-900 dark:text-gray-100">
          {feedback.message}
        </p>
      </td>
      <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300">{feedback.email ?? '-'}</td>
      <td className="px-4 py-3 align-top">
        <FeedbackStatusBadge status={feedback.status} />
        {feedback.reviewedAt && (
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {formatDate(feedback.reviewedAt)}
          </p>
        )}
      </td>
      <td className="px-4 py-3 align-top text-gray-700 dark:text-gray-300">{formatDate(feedback.createdAt)}</td>
      <td className="px-4 py-3 align-top">
        {canReview ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onReview(feedback, 'accepted')}
              disabled={isUpdating}
              className="rounded-md border border-green-300 px-2.5 py-1.5 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-green-800 dark:text-green-300 dark:hover:bg-green-950/40"
            >
              {isUpdating ? 'Saving...' : 'Accept'}
            </button>
            <button
              type="button"
              onClick={() => onReview(feedback, 'rejected')}
              disabled={isUpdating}
              className="rounded-md border border-red-300 px-2.5 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40"
            >
              Reject
            </button>
          </div>
        ) : (
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Reviewed</span>
        )}
      </td>
    </tr>
  );
}

interface FeedbackStatusBadgeProps {
  status: Feedback['status'];
}

function FeedbackStatusBadge({ status }: FeedbackStatusBadgeProps): JSX.Element {
  const classes = {
    pending: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200',
    accepted: 'bg-green-100 text-green-800 dark:bg-green-950/60 dark:text-green-200',
    rejected: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200',
  }[status];

  return (
    <span className={`rounded-md px-2 py-1 text-xs font-semibold ${classes}`}>
      {status}
    </span>
  );
}

interface BoardAdminRowProps {
  board: Board;
  isDeleting: boolean;
  onDelete: (board: Board) => void;
}

function BoardAdminRow({ board, isDeleting, onDelete }: BoardAdminRowProps): JSX.Element {
  const cardCount = countBoardCards(board);
  const handleDeleteClick = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    onDelete(board);
  };

  return (
    <details className="group">
      <summary className="flex cursor-pointer list-none flex-col gap-3 px-4 py-4 hover:bg-gray-50 dark:hover:bg-gray-800 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-white">{board.name}</h3>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            {cardCount} cards - {board.columns.length} columns - updated {formatDate(board.updatedAt ?? board.createdAt)}
          </p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <Link
            to={`/board/${board.id}`}
            className="rounded-md border border-gray-300 px-2.5 py-1.5 font-semibold text-gray-700 hover:bg-gray-100 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
            onClick={(event) => event.stopPropagation()}
          >
            Open
          </Link>
          <button
            type="button"
            onClick={handleDeleteClick}
            disabled={isDeleting}
            className="rounded-md border border-red-300 px-2.5 py-1.5 font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-red-800 dark:text-red-300 dark:hover:bg-red-950/40"
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
          <span className="text-xs font-semibold text-blue-700 group-open:hidden dark:text-blue-300">View content</span>
          <span className="hidden text-xs font-semibold text-blue-700 group-open:inline dark:text-blue-300">Hide content</span>
        </div>
      </summary>

      <div className="border-t border-gray-200 px-4 py-4 dark:border-gray-800">
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {board.columns.map((column) => (
            <div key={column.id} className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full"
                  style={{ backgroundColor: column.color }}
                  aria-hidden="true"
                />
                <h4 className="truncate text-sm font-semibold text-gray-900 dark:text-white">{column.name}</h4>
              </div>
              <div className="mt-3 space-y-2">
                {column.cards.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No cards.</p>
                ) : column.cards.map((card) => (
                  <AdminCard key={card.id} card={card} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </details>
  );
}

interface AdminCardProps {
  card: Card;
}

function AdminCard({ card }: AdminCardProps): JSX.Element {
  return (
    <article className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-800 dark:bg-gray-950">
      <p className="whitespace-pre-wrap text-gray-900 dark:text-gray-100">{card.content}</p>
      <p className="mt-2 text-xs font-medium text-gray-500 dark:text-gray-400">
        {card.author} - {formatDate(card.updatedAt ?? card.createdAt)}
      </p>
    </article>
  );
}

interface HeaderCellProps {
  children: ReactNode;
}

function HeaderCell({ children }: HeaderCellProps): JSX.Element {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {children}
    </th>
  );
}

interface UserRowProps {
  user: AdminUser;
}

function UserRow({ user }: UserRowProps): JSX.Element {
  return (
    <tr>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {user.pictureUrl ? (
            <img
              src={user.pictureUrl}
              alt=""
              className="h-8 w-8 rounded-full border border-gray-200 dark:border-gray-700"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-200 text-xs font-bold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
              {getInitials(user.name)}
            </div>
          )}
          <span className="font-medium text-gray-900 dark:text-white">{user.name}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{user.email ?? '-'}</td>
      <td className="px-4 py-3">
        <span className="rounded-md bg-gray-100 px-2 py-1 text-xs font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
          {user.role}
        </span>
      </td>
      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">{formatDate(user.updatedAt)}</td>
    </tr>
  );
}

function countBoardCards(board: Board): number {
  return board.columns.reduce((count, column) => count + column.cards.length, 0);
}

function getInitials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0][0]?.toUpperCase() ?? '?';
  return `${words[0][0] ?? ''}${words[words.length - 1][0] ?? ''}`.toUpperCase();
}

function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'unknown';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}
